import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { createVlanZonesApi } from '@/api/vendo';
import { parseZoneSpec, zoneSpecCount, MAX_ZONES_PER_BATCH } from '@/utils/vlanZones';

export interface CreateVlanZoneData {
   deviceId: string;
   /** MikroTik is pushed to; an OpenWRT (OrangePi) main pulls on its next heartbeat. */
   platform?: 'mikrotik' | 'openwrt';
   /** VLAN ids already on this vendo — used for an inline duplicate check. */
   existingIds: number[];
   /** Called after a successful create so the page can refetch its zones. */
   onCreated: () => void;
}

/**
 * Add VLAN zone(s) modal (VLAN redesign). ONE field takes a single id ("11")
 * or an inclusive range ("11-100"). Subnet + label are optional and apply
 * only to a single id — they auto-disable for a range (each VLAN in a range
 * gets its own auto-assigned /24, so a single custom subnet/label can't apply).
 * This removes the old simple/advanced split where bulk wouldn't work.
 */
const CreateVlanZone = ({ data }: { data: CreateVlanZoneData }) => {
   const { handleClose } = useUIStore();
   const { deviceId, platform, existingIds, onCreated } = data;

   const [spec, setSpec] = useState('');
   const [subnet, setSubnet] = useState('');
   const [label, setLabel] = useState('');
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);

   const parsed = parseZoneSpec(spec);
   const isRange = !!parsed && parsed.end > parsed.start;
   const isSingle = !!parsed && parsed.start === parsed.end;

   const submit = async () => {
      setErr('');
      if (!parsed) {
         setErr('Enter a single VLAN like "11" or a small range like "11-13" (10–4094).');
         return;
      }
      const count = zoneSpecCount(spec) ?? 0;
      if (count > MAX_ZONES_PER_BATCH) {
         setErr(`Add at most ${MAX_ZONES_PER_BATCH} VLANs at a time.`);
         return;
      }
      if (isSingle && existingIds.includes(parsed.start)) {
         setErr(`VLAN ${parsed.start} already exists on this vendo.`);
         return;
      }
      try {
         setBusy(true);
         // Single id + a custom subnet/label → explicit single create.
         // Otherwise (range, or single with no extras) → spec create with
         // auto-assigned subnet(s).
         const payload =
            isSingle && (subnet.trim() || label.trim())
               ? {
                    vlanId: parsed.start,
                    ...(subnet.trim() ? { subnet: subnet.trim() } : {}),
                    ...(label.trim() ? { label: label.trim() } : {}),
                 }
               : { spec: spec.trim() };
         const res = await createVlanZonesApi(deviceId, payload);
         // Zones are saved as "pending" — be honest that they aren't live yet.
         // MikroTik: pushed in the background. OpenWRT: the router reads them
         // on its next heartbeat, nothing is pushed.
         toast.success(
            platform === 'openwrt'
               ? `${res.msg}. The router applies them on its next check-in (about a minute) — they turn green when it confirms.`
               : `${res.msg}. Pushing to the router — they turn green when confirmed.`
         );
         onCreated();
         handleClose();
      } catch (e: any) {
         setErr(e?.response?.data?.error || 'Failed to create zone(s).');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Form>
         <FieldCol>
            <FieldLabel htmlFor="vz-spec">VLAN ID or range *</FieldLabel>
            <Input
               id="vz-spec"
               autoFocus
               placeholder='e.g. "11" or "11-13"'
               value={spec}
               $invalid={!!err}
               onChange={(e) => {
                  setSpec(e.target.value);
                  if (err) setErr('');
               }}
               onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
            <Hint>
               Subnets are auto-assigned to match the VLAN id (VLAN 51 →
               10.51.0.0/24). Add up to {MAX_ZONES_PER_BATCH} at a time.
            </Hint>
         </FieldCol>

         <Row>
            <FieldCol>
               <FieldLabel htmlFor="vz-subnet">Subnet (optional)</FieldLabel>
               <Input
                  id="vz-subnet"
                  disabled={!isSingle}
                  placeholder={isRange ? 'auto for ranges' : 'auto · or 10.50.0.0/24'}
                  value={subnet}
                  onChange={(e) => setSubnet(e.target.value)}
               />
            </FieldCol>
            <FieldCol>
               <FieldLabel htmlFor="vz-label">Label (optional)</FieldLabel>
               <Input
                  id="vz-label"
                  disabled={!isSingle}
                  placeholder={isRange ? 'one VLAN at a time' : 'e.g. Cafe area'}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
               />
            </FieldCol>
         </Row>
         {isRange && (
            <Hint>
               Custom subnet/label apply to a single VLAN only — add one at a
               time to set them.
            </Hint>
         )}

         {err && <ErrText role="alert">{err}</ErrText>}

         <Actions>
            <GhostBtn type="button" onClick={handleClose}>
               Cancel
            </GhostBtn>
            <PrimaryBtn type="button" disabled={busy || !deviceId} onClick={submit}>
               {busy ? 'Adding…' : 'Add zone(s)'}
            </PrimaryBtn>
         </Actions>
      </Form>
   );
};

export default CreateVlanZone;

/* -------------------------------- styles -------------------------------- */
const Form = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   min-width: 420px;
   max-width: 560px;
   @media (max-width: 560px) {
      min-width: unset;
      width: 100%;
   }
`;

const Row = styled.div`
   display: flex;
   gap: 12px;
   @media (max-width: 480px) {
      flex-direction: column;
   }
`;

const FieldCol = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   flex: 1;
`;

const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const Input = styled.input<{ $invalid?: boolean }>`
   padding: 10px 12px;
   border: 1px solid ${(p) => (p.$invalid ? colors.DANGER : `${colors.PRIMARY}30`)};
   border-radius: 8px;
   font-size: 13px;
   width: 100%;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
   &:disabled {
      background: ${colors.GRAY1}10;
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

const Hint = styled.span`
   font-size: 11px;
   color: ${colors.GRAY1};
   line-height: 1.4;
`;

const ErrText = styled.div`
   color: ${colors.DANGER};
   font-size: 12px;
   font-weight: 600;
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

const PrimaryBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 10px 20px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
   &:hover:not(:disabled) {
      opacity: 0.9;
   }
`;

const GhostBtn = styled.button`
   background: transparent;
   color: ${colors.GRAY1};
   border: 1px solid ${colors.GRAY1}50;
   border-radius: 8px;
   padding: 10px 20px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      background: ${colors.GRAY1}10;
   }
`;
